import React, { useState } from 'react';
import { Box } from '@mui/system';
import { motion, AnimatePresence } from 'framer-motion';
import { useFormik } from 'formik';
import formValidation from '../formValidation';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Grid, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ProfessionRadio from './ProfessionRadio';
import AccreditingBodyRadio from './AccreditingBodyRadio';
import emailjs from '@emailjs/browser';
import { useNavigate } from 'react-router-dom';

function Form() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  // Framer animation options
  const animationOptions = {
    initial: { y: -30, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: {
      y: -30,
      opacity: 0,
      transition: { duration: 0.1 },
    },
    transition: { duration: 0.2, ease: 'easeOut' },
  };

  // Formik
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      institution: '',
      profession: '',
      professionOther: '',
      accreditingBody: '',
      accreditingBodyOther: '',
    },
    validationSchema: formValidation,
    onSubmit: async (values, actions) => {
      setLoading(true);

      const docRef = await addDoc(collection(db, 'forms'), {
        ...values,
        timestamp: serverTimestamp(),
      });

      // Send email
      await emailjs.send(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        values,
        process.env.REACT_APP_API_KEY
      );

      // Clean up and navigate
      actions.resetForm();
      setLoading(false);
      navigate('/success');
    },
  });

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={4}>
          <Grid xs={12} sm={6} item>
            <TextField
              label="First Name"
              id="firstName"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
              placeholder="Enter first name"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={6} item>
            <TextField
              label="Last Name"
              id="lastName"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              placeholder="Enter last name"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid xs={12} item>
            <TextField
              label="Email"
              id="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              placeholder="Enter email"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid xs={12} item>
            <TextField
              label="Institution (organization)"
              id="institution"
              name="institution"
              value={formik.values.institution}
              onChange={formik.handleChange}
              error={
                formik.touched.institution && Boolean(formik.errors.institution)
              }
              helperText={
                formik.touched.institution && formik.errors.institution
              }
              placeholder="Enter institution"
              variant="outlined"
              fullWidth
            />
          </Grid>

          <ProfessionRadio
            value={formik.values.profession}
            onChange={formik.handleChange}
            error={
              formik.touched.profession && Boolean(formik.errors.profession)
            }
          />

          <AnimatePresence>
            {formik.values.profession === 'Other' && (
              <Grid
                key="professionOther"
                xs={12}
                item
                component={motion.div}
                {...animationOptions}
              >
                <TextField
                  label="If other, please specify"
                  name="professionOther"
                  id="professionOther"
                  value={formik.values.professionOther}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.professionOther &&
                    Boolean(formik.errors.professionOther)
                  }
                  helperText={
                    formik.touched.professionOther &&
                    formik.errors.professionOther
                  }
                  placeholder="Specify your profession"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {formik.values.profession === 'Physician' && (
              <AccreditingBodyRadio
                value={formik.values.accreditingBody}
                onChange={formik.handleChange}
                error={
                  formik.touched.accreditingBody &&
                  Boolean(formik.errors.accreditingBody)
                }
                animation={animationOptions}
                component={motion.div}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {formik.values.accreditingBody === 'Other' &&
              formik.values.profession === 'Physician' && (
                <>
                  <Grid
                    key="accreditingBodyOther"
                    xs={12}
                    item
                    component={motion.div}
                    {...animationOptions}
                  >
                    <TextField
                      label="If other, please specify"
                      name="accreditingBodyOther"
                      id="accreditingBodyOther"
                      value={formik.values.accreditingBodyOther}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.accreditingBodyOther &&
                        Boolean(formik.errors.accreditingBodyOther)
                      }
                      helperText={
                        formik.touched.accreditingBodyOther &&
                        formik.errors.accreditingBodyOther
                      }
                      placeholder="Specify the accrediting body"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                </>
              )}
          </AnimatePresence>

          <Grid xs={12} item>
            <LoadingButton
              loading={loading}
              type="submit"
              variant="contained"
              fullWidth
            >
              Enroll
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default Form;
